import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { localizedAlternates, localizedPath, useLang } from "@/lib/i18n";
import { instrumentCoverSrc } from "@/lib/instrumentSeo";
import api from "@/lib/apiClient";
import useSEO from "@/lib/useSEO";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Clock, Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const badgeMap = {
  explorasi: { id: "Explorasi", en: "Exploration" },
  dalam_pengembangan: { id: "Dalam Pengembangan", en: "In Development" },
  berbasis_kriteria: { id: "Berbasis Kriteria", en: "Criterion-based" },
  norma_sementara: { id: "Norma Sementara", en: "Interim Norm" },
  tervalidasi: { id: "Tervalidasi", en: "Validated" },
};

export default function Catalogue() {
  const { t, lang, pick } = useLang();
  const [instruments, setInstruments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const catSlug = params.get("category") || "all";

  useSEO({
    title: lang === "id" ? "Semua Tes — Mindovia" : "All Tests — Mindovia",
    description: lang === "id"
      ? "Jelajahi seluruh instrumen refleksi diri di Mindovia — dari Bahasa Cinta hingga Kepribadian Lima Besar. Anonim, singkat, dan bisa dikerjakan langsung tanpa mendaftar."
      : "Explore all self-reflection instruments on Mindovia — from Love Languages to Big Five Personality. Anonymous, short, and available without signup.",
    url: localizedPath("/instruments", lang),
    alternates: localizedAlternates("/instruments"),
    image: "/og-cover.png",
  });

  useEffect(() => {
    api.get("/public/instruments").then(r => setInstruments(r.data)).catch(() => {});
    api.get("/public/categories").then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return instruments.filter((i) => {
      const inCat = catSlug === "all" || (i.categories || []).some(c => c.slug === catSlug) || i.category?.slug === catSlug;
      const text = `${i.name_id} ${i.name_en} ${i.short_description_id} ${i.short_description_en}`.toLowerCase();
      const inQ = q.trim() === "" || text.includes(q.toLowerCase());
      return inCat && inQ;
    });
  }, [instruments, catSlug, q]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-14">
      <div className="mb-10 space-y-3">
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">{t("cat.title")}</h1>
        <p className="text-muted-foreground max-w-2xl">
          {lang === "id"
            ? "Pilih tema, baca penjelasannya, lalu mulai kapan pun kamu siap."
            : "Pick a theme, read the details, and start whenever you're ready."}
        </p>
      </div>
      <div className="flex flex-col md:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="catalogue-search"
            placeholder={t("cat.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10 h-11 bg-card"
          />
        </div>
        <Select value={catSlug} onValueChange={(v) => { const p = new URLSearchParams(params); if (v === "all") p.delete("category"); else p.set("category", v); setParams(p); }}>
          <SelectTrigger data-testid="catalogue-category-filter" className="w-full md:w-64 h-11 bg-card">
            <SelectValue placeholder={t("cat.all")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("cat.all")}</SelectItem>
            {categories.filter(c => c.slug && c.slug.trim()).map((c) => (
              <SelectItem key={c.id} value={c.slug}>{pick(c, "name")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {filtered.length === 0 ? (
        <div className="py-24 text-center text-muted-foreground" data-testid="catalogue-empty">{t("cat.empty")}</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((inst) => {
            const b = badgeMap[inst.development_status] || badgeMap.explorasi;
            return (
              <Link
                key={inst.id}
                to={localizedPath(`/instruments/${inst.slug}`, lang)}
                data-testid={`instrument-card-${inst.slug}`}
                className="group rounded-2xl border border-border bg-card overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="aspect-[4/5] overflow-hidden bg-secondary">
                  <img src={instrumentCoverSrc(inst, lang)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-terracotta-200 text-terracotta-700 bg-terracotta-50">
                      {b[lang]}
                    </Badge>
                    {inst.category && (
                      <span className="text-xs text-muted-foreground">{pick(inst.category, "name")}</span>
                    )}
                  </div>
                  <h3 className="font-display text-xl leading-snug">{pick(inst, "name")}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">{pick(inst, "short_description")}</p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {inst.duration_minutes} {t("cat.min")}</span>
                    <span>{inst.question_count} {t("cat.questions")}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
